/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Slider, {SliderProps} from '@material-ui/core/Slider';
import {withStyles} from '@material-ui/core/styles';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';

export interface SliderTypes extends SliderProps {
  onSlide?: (event: any, value: number | number[]) => void,
  hideThumb?: boolean;
  input?: boolean;
  progress?: boolean;
  // current-theme-only prop (full-width slider track) - accepted here
  // too so it's never spread onto the underlying MUI Slider's DOM node ...
  fullWidth?: boolean;
}

// [DLS PACKAGE TODO]: The below styles can safely be ignored as they are implemented
// as a function (ignored due to limitations of material-ui-verifier AST back referencing)
/* eslint-disable ion-eslint-rules/material-ui-verifier */
const styles = theme => ({
  thumb : {
    display                      : props => props.hideThumb ? 'none !important' : 'flex',
    height                       : 15,
    width                        : 15,
    backgroundColor              : `${dlsColor('aaBlue')}`,
    marginTop                    : -6,
    '&:focus, &:hover, &$active' : {
      boxShadow :
        '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)'
    },
    '&$disabled' : {
      height          : 15,
      width           : 15,
      marginTop       : -6,
      backgroundColor : `${dlsColor('grey4')}`
    }
  },
  active   : {},
  disabled : {},
  track    : {
    height          : props => props.progress ? 8 : 4,
    width           : 6,
    backgroundColor : `${dlsColor('aaBlue')}`,
    borderRadius    : props => props.progress ? 0 : 6,
    '$disabled &'   : {
      backgroundColor : `${dlsColor('grey4')}`
    }
  },
  rail : {
    height          : props => props.progress ? 8 : 4,
    opacity         : props => props.input ? 1 : 0.2,
    borderRadius    : props => props.progress ? 0 : 6,
    backgroundColor : props => props.input ? dlsColor('grey5') : dlsColor('aaBlue'),
    '$disabled &'   : {
      backgroundColor : `${dlsColor('grey4')}`
    }
  },
  mark : {
    opacity : 0
  },
  markLabel : {
    ...dlsTypography('slider')
  }
});

/**
 * Defines the Slider component. This component is reusable and can be custom
 * rendered with props. Prop input is optional if user pass input as prop will apply
 * grey5 color for rail and by default lightblue color will be applied for rail of slider.
 *
 * ```typescript
 *   const marks = [{
 *     value: 0,
 *     label: '0',
 *   }, {
 *     value: 0.5,
 *     label: '50',
 *   }, {
 *     value: 1,
 *     label: '100',
 *   }];
 *
 *   <Slider step={0.1}
       marks={marks}
       max={1}
       value={value}
       hideThumb
       input
       onSlide={function(){}} />
 *
 * ```
 */
export const SliderWrapper = ({
  onSlide,
  hideThumb = false,
  input = false,
  progress = false,
  fullWidth = false,
  ...props
}: SliderTypes) => {
  return (
    <Slider onChange={onSlide} {...props} />
  );
};

export default withStyles(styles)(SliderWrapper);
